import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  ShieldOutlined,
  SchoolOutlined,
  AdminPanelSettingsOutlined,
  LockOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useLoginStyle from "../LoginStyle/LoginStyle";
import { loginApi, loginApiWithoutPassword, sendOtpApi, verifyOtpApi, updatePasswordApi } from "../../../services/service";
import { toast } from "react-toastify";
import ROUTES_NAME from "../../../constants/RoutesName";
import Loader from "../../../components/Loader";
import socket from "../../../socket/socket";
import { useProfile } from "../../../context/ProfileContext";
import logo from "../../../images/topranker.jpg";
import OtpPopup from "./OtpPopup";
import ChangePasswordPopup from "./ChangePasswordPopup";

function LoginDefault({ handleLogin }) {
  const classes = useLoginStyle();
  const navigate = useNavigate();
  const [error, setError] = useState("Please enter your credentials");
  const [formData, setFormData] = useState({
    mobileOrRoll: "",
    password: "",
  });
  const { setProfile } = useProfile();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState("otp"); // Default login mode set to 'otp'
  const [openOtpPopup, setOpenOtpPopup] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [pendingAuth, setPendingAuth] = useState(null);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "pin") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 4) {
        setFormData({ ...formData, [name]: numericValue });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  function getBaseUrl() {
    const url = new URL(window.location.href);
    return `${url.protocol}//${url.host}`;
  }

  const completeLogin = (username, authSecret, isOtpMode) => {
    sessionStorage.setItem("candidateLoggedinToDashboard", "true");
    sessionStorage.setItem(
      "userAuth",
      JSON.stringify(
        isOtpMode
          ? { username, otp: authSecret }
          : { username, password: authSecret }
      )
    );
    toast.success("Login Successful!");
    handleLogin();
    navigate(ROUTES_NAME.DASHBOARD);
  };


  const handleSendOtp = async () => {
    const { mobileOrRoll } = formData;
    if (!mobileOrRoll) {
      toast.error("Please enter Mobile number");
      return;
    }
    setLoading(true);
    try {
      const response = await sendOtpApi({ phone: mobileOrRoll });
      if (response && response.success) {
        toast.success("Verification code sent successfully!");
        setOpenOtpPopup(true);
      } else {
        toast.error(response?.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (otpValue) => {
    const { mobileOrRoll } = formData;
    setLoading(true);
    try {
      const result = await verifyOtpApi({ phone: mobileOrRoll, otp: otpValue });
      if (result && result.success) {
        setProfile(result?.learner);

        if (result?.learner?.is_first_login === 1) {
          sessionStorage.removeItem("candidateLoggedinToDashboard");
          setPendingAuth({
            username: mobileOrRoll,
            secret: otpValue,
            isOtp: true
          });
          setOpenOtpPopup(false);
          setTimeout(() => {
            setOpenChangePassword(true);
          }, 200);
        } else {
          setOpenOtpPopup(false);
          completeLogin(mobileOrRoll, otpValue, true);
        }
      } else {
        toast.error(result?.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      toast.error(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpResend = async () => {
    const { mobileOrRoll } = formData;
    if (!mobileOrRoll) {
      toast.error("Please enter Mobile number");
      return;
    }
    setLoading(true);
    try {
      const response = await sendOtpApi({ phone: mobileOrRoll });
      if (response && response.success) {
        toast.success("OTP resent successfully to " + mobileOrRoll);
      } else {
        toast.error(response?.message || "Failed to resend OTP");
      }
    } catch (err) {
      toast.error(err.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (loginMode === "otp") {
      await handleSendOtp();
      return;
    }

    const { mobileOrRoll, password, pin } = formData;

    if (!mobileOrRoll) {
      toast.error("Please enter Mobile number");
      return;
    }
    if (!password) {
      toast.error("Please enter password");
      return;
    }

    setLoading(true);
    try {
      const result = await loginApiWithoutPassword({
        username: mobileOrRoll,
        password: password,
      });

      setProfile(result?.learner);

      if (result.success) {
        setLoading(false);

        if (result?.learner?.is_first_login === 1) {
          sessionStorage.removeItem("candidateLoggedinToDashboard");
          setPendingAuth({
            username: mobileOrRoll,
            secret: password,
            isOtp: false
          });
          setTimeout(() => {
            setOpenChangePassword(true);
          }, 200);
        } else {
          completeLogin(mobileOrRoll, password, false);
        }
      } else {
        setLoading(false);
        setError(result.message || "An error occurred");
        toast.error(result.message || "An error occurred");
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || "An error occurred");
      toast.error(err.message || "An error occurred");
    }
  };

  useEffect(() => {
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <Box
      className={classes.loginContainer}
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 2,
        background: `
          radial-gradient(circle at top left, rgba(25, 118, 210, 0.12), transparent 28%),
          radial-gradient(circle at bottom right, rgba(46, 125, 50, 0.10), transparent 24%),
          linear-gradient(135deg, #f4f7fb 0%, #eef3f8 45%, #f8fafc 100%)
        `,
      }}
    >
      <Paper
        elevation={0}
        className={classes.loginPaper}
        sx={{
          width: "100%",
          maxWidth: 400,
          overflow: "hidden",
          borderRadius: "24px",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          boxShadow:
            "0 15px 40px rgba(15, 23, 42, 0.08), 0 5px 15px rgba(15, 23, 42, 0.04)",
          background: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(10px)",
          p: { xs: 2.5, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.98) 100%)",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
              <img src={logo} alt="logo" style={{ maxWidth: 260, width: "100%", height: "auto" }} />
            </Box>

            <Typography
              variant="h4"
              className={classes.loginTitle}
              gutterBottom
              sx={{
                fontWeight: 800,
                color: "#0f172a",
                fontSize: { xs: "1.5rem", sm: "1.7rem" },
                letterSpacing: "-0.02em",
                mb: 0.5,
                textAlign: "center",
              }}
            >
              Login
            </Typography>

            <Box
              sx={{
                mb: 2,
                px: 1.8,
                py: 0.8,
                borderRadius: "12px",
                background:
                  error && error !== "Please enter your credentials"
                    ? "rgba(239,68,68,0.06)"
                    : "rgba(15,23,42,0.03)",
                border:
                  error && error !== "Please enter your credentials"
                    ? "1px solid rgba(239,68,68,0.12)"
                    : "1px solid rgba(15,23,42,0.04)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color:
                    error && error !== "Please enter your credentials"
                      ? "#b91c1c"
                      : "#475569",
                  fontWeight: 500,
                  fontSize: "0.85rem",
                  textAlign: "center",
                }}
              >
                {error}
              </Typography>
            </Box>

            <Box mt={1.5}>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  mb: 0.5,
                  color: "#334155",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                }}
              >
               Mobile Number
              </Typography>

              <TextField
                fullWidth
                name="mobileOrRoll"
                placeholder="Enter Mobile Number"
                value={formData.mobileOrRoll}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 48,
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    transition: "all 0.25s ease",
                    "& fieldset": {
                      borderColor: "rgba(148,163,184,0.25)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(25,118,210,0.35)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: "1.5px",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 0 0 3px rgba(25,118,210,0.06)",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.92rem",
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: "#64748b", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {loginMode === "password" && (
              <Box mt={1.5}>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{
                    mb: 0.5,
                    color: "#334155",
                    fontWeight: 700,
                    fontSize: "0.88rem",
                  }}
                >
                  Password
                </Typography>

                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 48,
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                      transition: "all 0.25s ease",
                      "& fieldset": {
                        borderColor: "rgba(148,163,184,0.25)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(25,118,210,0.35)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#1976d2",
                        borderWidth: "1.5px",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 3px rgba(25,118,210,0.06)",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.92rem",
                      fontWeight: 500,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: "#64748b", fontSize: "1.2rem" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ color: "#64748b" }}
                          size="small"
                        >
                          {showPassword ? <Visibility sx={{ fontSize: "1.2rem" }} /> : <VisibilityOff sx={{ fontSize: "1.2rem" }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            <Box mt={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="text"
                onClick={() => {
                  setLoginMode(loginMode === "password" ? "otp" : "password");
                  setError("Please enter your credentials");
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#1565c0",
                  fontSize: "0.85rem",
                  py: 0.5,
                  "&:hover": {
                    backgroundColor: "rgba(21, 101, 192, 0.04)",
                  },
                }}
              >
                {loginMode === "password" ? "Login with OTP" : "Login with Password"}
              </Button>
            </Box>

            <Button
              variant="contained"
              fullWidth
              className={classes.loginButton}
              onClick={handleSubmit}
              sx={{
                mt: 1.5,
                height: 48,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                letterSpacing: "0.01em",
                background: "linear-gradient(135deg, #1565c0 0%, #0f4fa8 100%)",
                boxShadow: "0 10px 24px rgba(21,101,192,0.18)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0f5ab2 0%, #0b4695 100%)",
                  boxShadow: "0 12px 28px rgba(21,101,192,0.25)",
                },
              }}
            >
              {loginMode === "password" ? "Sign In to Panel" : "Send OTP"}
            </Button>

            <Divider sx={{ my: 2, borderColor: "rgba(148,163,184,0.22)" }} />

            <Typography
              className={classes.versionText}
              sx={{
                mt: 3,
                textAlign: "center",
                color: "#94a3b8",
                fontSize: "0.83rem",
                fontWeight: 500,
              }}
            >
              Version 1.5.0 | Dated - 13 JUNE 2026
            </Typography>
          </Box>
        </Box>
      </Paper>

      {loading && <Loader />}

      <OtpPopup
        open={openOtpPopup}
        onClose={() => setOpenOtpPopup(false)}
        mobileNumber={formData.mobileOrRoll}
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
      />

      <ChangePasswordPopup
        open={openChangePassword}
        pendingAuth={pendingAuth}
        onComplete={(newPass) => {
          setOpenChangePassword(false);
          completeLogin(pendingAuth.username, newPass, false);
        }}
      />
    </Box>
  );
}

export default LoginDefault;